import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/common/Layout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { ToastProvider } from '@/components/ui/Toast';
import { useAuthStore } from '@/store/auth.store';
import Login from '@/pages/login/Login';
import EntrarGlobal from '@/pages/entrar-global/EntrarGlobal';
import AcceptInvite from '@/pages/auth/AcceptInvite';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import TecnicoJobs from '@/pages/tecnico/TecnicoJobs';
import Dashboard from '@/pages/dashboard/Dashboard';
import Agents from '@/pages/agents/Agents';
import Channels from '@/pages/channels/Channels';
import Conversations from '@/pages/conversations/Conversations';
import ConversationDetail from '@/pages/conversations/ConversationDetail';
import Crm from '@/pages/crm/Crm';
import Contacts from '@/pages/contacts/Contacts';
import Cartera from '@/pages/cartera/Cartera';
import Campaigns from '@/pages/campaigns/Campaigns';
import ModulosDelNegocio from '@/pages/ajustes/Modulos';
import Productos from '@/pages/productos/Productos';
import ImportarInventario from '@/pages/productos/ImportarInventario';
import Inventario from '@/pages/inventario/Inventario';
import Ventas from '@/pages/ventas/Ventas';
import Finanzas from '@/pages/finanzas/Finanzas';
import Clientas from '@/pages/clientas/Clientas';
import Pacas from '@/pages/pacas/Pacas';
import Bitacora from '@/pages/bitacora/Bitacora';
import Propuestas from '@/pages/propuestas/Propuestas';
import Settings from '@/pages/settings/Settings';
import { InstallationConfigPage } from '@/pages/installation/InstallationConfigPage';
import Users from '@/pages/users/Users';
import Employees from '@/pages/employees/Employees';
import Jobs from '@/pages/jobs/Jobs';
import Despachos from '@/pages/despachos/Despachos';
import Tracking from '@/pages/tracking/Tracking';
import Clientes from '@/pages/clientes/Clientes';
import ClienteDetail from '@/pages/clientes/ClienteDetail';
import Sistema from '@/pages/sistema/Sistema';
import Entrenamiento from '@/pages/entrenamiento/Entrenamiento';
import Consumos from '@/pages/consumos/Consumos';
import Limites from '@/pages/limites/Limites';
import Inspector from '@/pages/inspector/Inspector';
import InspectorAjustes from '@/pages/inspector/InspectorAjustes';
import NuevoNegocio from '@/pages/negocios/NuevoNegocio';
import Agenda from '@/pages/agenda/Agenda';
import Seguimientos from '@/pages/seguimientos/Seguimientos';
import Proyectos from '@/pages/proyectos/Proyectos';
import MasterclassPage from '@/pages/masterclass/Masterclass';
import Publicidad from '@/pages/publicidad/Publicidad';
import Inicio from '@/pages/Inicio';
import TablaDemo from '@/pages/demo/TablaDemo';
import MeToca from '@/pages/metoca/MeToca';
import Personas from '@/pages/personas/Personas';
import FichaDePersona from '@/pages/personas/FichaDePersona';
import AthenaPagina from '@/pages/athena/Athena';
import ChatDeAthena from '@/pages/chat/ChatDeAthena';
import Chats from '@/pages/chats/Chats';
import FrentesPagina from '@/pages/frentes/Frentes';
import Importar from '@/pages/importar/Importar';
import ImportarContactos from '@/pages/contacts/ImportarContactos';
import Salud from '@/pages/salud/Salud';
import ClaveDeIA from '@/pages/ajustes/ClaveDeIA';
import Cola from '@/pages/cola/Cola';
import Metricas from '@/pages/cola/Metricas';
import Simulador from '@/pages/simulador/Simulador';
import AjustesDelAgente from '@/pages/ajustes/AjustesDelAgente';

/**
 * Shell admin con guardia de rol: un TECNICO no ve el CRM. Se le rebota a su
 * vista acotada (/mis-trabajos). El resto entra al <Layout> completo.
 */
function AdminShell() {
  const role = useAuthStore((s) => s.user?.role);
  if (role === 'TECNICO') return <Navigate to="/mis-trabajos" replace />;
  return <Layout />;
}

/** Router raíz de Conversia. Rutas privadas bajo <Layout> protegido. */
export default function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Entrar desde el Conversia global con un pase de un minuto (pages/entrar-global). */}
        <Route path="/entrar-global" element={<EntrarGlobal />} />
        {/* Ciclo de cuentas (páginas públicas): canje de invitación y reset. */}
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Vista del técnico: standalone (sin sidebar/CRM), solo requiere sesión. */}
        <Route
          path="/mis-trabajos"
          element={
            <ProtectedRoute>
              <TecnicoJobs />
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <AdminShell />
            </ProtectedRoute>
          }
        >
          {/* La raiz NO es una pantalla fija: `Inicio` mira el menu que sirve
              el servidor. Los negocios con `inspector` entran por HOY (el
              mandato Athena saca el Dashboard de ventas de este producto; sigue
              en /dashboard). Los que no lo tienen —una tienda, por ejemplo—
              entraban a una pantalla de un modulo que no compraron, con cuatro
              404 en cada carga. Ahora entran por la primera pantalla que SI
              tienen. */}
          <Route path="/" element={<Inicio />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/me-toca" element={<MeToca />} />
          <Route path="/personas" element={<Personas />} />
          <Route path="/personas/:id" element={<FichaDePersona />} />
          <Route path="/athena" element={<AthenaPagina />} />
          <Route path="/chats" element={<Chats />} />
          <Route path="/chats/:id" element={<ChatDeAthena />} />
          <Route path="/frentes" element={<FrentesPagina />} />
          <Route path="/importar" element={<Importar />} />
          <Route path="/salud" element={<Salud />} />
          {/* OWNER y no ADMIN: la API de claves solo acepta al dueño. Un admin
              que entra acá veria el formulario y recibiria un 403 al guardar. */}
          <Route
            path="/ajustes/clave-ia"
            element={
              <ProtectedRoute requireRole="OWNER" requireRuta="/ajustes/clave-ia">
                <ClaveDeIA />
              </ProtectedRoute>
            }
          />
          <Route path="/conversations" element={<Conversations />} />
          <Route path="/conversations/:id" element={<ConversationDetail />} />
          <Route path="/crm" element={<Crm />} />
          {/* 8-oct: la configuración del agente y del sistema es de la administración. `requireRuta`
              le pregunta al servidor: para el dueño del negocio estas pantallas no existen. */}
          <Route path="/agents" element={<ProtectedRoute requireRuta="/agents"><Agents /></ProtectedRoute>} />
          <Route path="/channels" element={<Channels />} />
          {/* Tanda 5: «Clientes», la cartera completa A–Z. Protegida como /contacts. */}
          <Route path="/cartera" element={<Cartera />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/contacts/importar" element={<ImportarContactos />} />
          <Route path="/contacts/importar-chat" element={<Importar variante="dueno" />} />
          <Route path="/proyectos" element={<ProtectedRoute requireRole="ADMIN"><Proyectos /></ProtectedRoute>} />
          <Route path="/masterclass" element={<ProtectedRoute requireRole="ADMIN"><MasterclassPage /></ProtectedRoute>} />
          {/* El analista de anuncios: cuelga de «Reportes». La API también lo exige. */}
          <Route path="/publicidad" element={<ProtectedRoute requireRole="ADMIN" requireRuta="/publicidad"><Publicidad /></ProtectedRoute>} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/ajustes/modulos" element={<ProtectedRoute requireRuta="/ajustes/modulos"><ModulosDelNegocio /></ProtectedRoute>} />
          <Route path="/productos" element={<Productos />} />
          {/* Validacion de la Fase 1. Se borra cuando los modulos reales usen estos componentes. */}
          {/* LA PÁGINA DE DEMOSTRACIÓN, SOLO EN DESARROLLO.
              No está en el menú de nadie, pero el guard de módulos vive en la
              API: escribiendo la dirección se abría igual, en el panel del
              cliente. Un banco de pruebas de componentes con los datos reales
              del negocio no es algo que Maciel deba poder encontrar. */}
          {import.meta.env.DEV && <Route path="/tabla-demo" element={<TablaDemo />} />}
          <Route path="/productos/importar" element={<ImportarInventario />} />
          <Route path="/inventario" element={<Inventario />} />
          <Route path="/ventas" element={<Ventas />} />
          <Route path="/finanzas" element={<Finanzas />} />
          {/* Tienda: clientas, pacas y las dos pantallas de control. `/clientas`
              no es `/clientes` — esa otra es la de ordenes de trabajo. */}
          <Route path="/clientas" element={<Clientas />} />
          <Route path="/pacas" element={<Pacas />} />
          <Route path="/bitacora" element={<Bitacora />} />
          <Route path="/propuestas" element={<Propuestas />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requireRole="ADMIN" requireRuta="/settings">
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/negocios/nuevo"
            element={
              <ProtectedRoute requireRole="OWNER" requireRuta="/negocios/nuevo">
                <NuevoNegocio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agenda"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Agenda />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seguimientos"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Seguimientos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/zooms"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Agenda soloZoom />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspector"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Inspector />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspector/ajustes"
            element={
              <ProtectedRoute requireRole="ADMIN" requireRuta="/inspector/ajustes">
                <InspectorAjustes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/installation"
            element={
              <ProtectedRoute requireRole="ADMIN" requireRuta="/installation">
                <InstallationConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Employees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Jobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/despachos"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Despachos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Clientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes/:id"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ClienteDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Tracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              // OWNER, igual que su API: las rutas de usuarios no distinguen
              // negocio, y en manos de un ADMIN permitían resetear la clave del
              // dueño. Ver users.routes.ts.
              <ProtectedRoute requireRole="OWNER">
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sistema"
            element={
              <ProtectedRoute requireRole="ADMIN" requireRuta="/sistema">
                <Sistema />
              </ProtectedRoute>
            }
          />
          {/* Tanda 3: el simulador de la demo. La API solo registra sus rutas con
              SIMULADOR_PERMITIDO=1; sin la bandera, esta pantalla queda sin datos y el
              menú no la ofrece. */}
          <Route
            path="/simulador"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Simulador />
              </ProtectedRoute>
            }
          />
          {/* Tanda 3 (F19): los ajustes del dueño. El menú la ofrece solo si hay guion configurado. */}
          <Route
            path="/ajustes/agente"
            element={
              <ProtectedRoute requireRole="ADMIN" requireRuta="/ajustes/agente">
                <AjustesDelAgente />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metricas"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Metricas />
              </ProtectedRoute>
            }
          />
          {/* Relevo de cola (F5.6, E-012). La API responde 404 si el negocio
              no tiene el módulo; el menú tampoco lo ofrece. */}
          <Route
            path="/cola"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Cola />
              </ProtectedRoute>
            }
          />
          <Route
            path="/entrenamiento"
            element={
              <ProtectedRoute requireRole="ADMIN" requireRuta="/entrenamiento">
                <Entrenamiento />
              </ProtectedRoute>
            }
          />
          {/* Reservado del OWNER (F2): consumo USD y límites de gasto. La API
              ya responde 403 a un ADMIN; esto solo evita mostrarle una página
              rota. */}
          <Route
            path="/consumos"
            element={
              <ProtectedRoute requireRole="OWNER" requireRuta="/consumos">
                <Consumos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/limites"
            element={
              <ProtectedRoute requireRole="OWNER" requireRuta="/limites">
                <Limites />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastProvider />
    </>
  );
}
